import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  Zap, Wifi, Smartphone, Shield, CreditCard, 
  CheckCircle2, ChevronRight, Star, TrendingDown,
  Award, Users, Clock, ExternalLink, Sparkles, Gift
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PublicLayout from "@/components/layout/PublicLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  "strom": Zap,
  "dsl-internet": Wifi,
  "handyvertrag": Smartphone,
  "versicherungen": Shield,
  "kredit-kreditkarte": CreditCard,
};

const categoryColors = {
  "strom": "from-yellow-400 to-orange-500",
  "dsl-internet": "from-blue-400 to-indigo-500",
  "handyvertrag": "from-pink-400 to-rose-500",
  "versicherungen": "from-emerald-400 to-teal-500",
  "kredit-kreditkarte": "from-purple-400 to-violet-500",
};

// Format large numbers
const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace('.', ',') + ' Mio';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(0) + '.000';
  }
  return num.toLocaleString('de-DE');
};

export default function HomePage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteStats, setSiteStats] = useState({
    providers: 1000,
    users: 2000000,
    average_savings: 540,
    total_clicks: 0
  });
  const [dailyDeals, setDailyDeals] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/public/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/analytics/stats`);
        setSiteStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    const fetchDailyDeals = async () => {
      try {
        const response = await axios.get(`${API}/public/daily-recommendations`);
        setDailyDeals(response.data || []);
      } catch (error) {
        console.error("Error fetching daily deals:", error);
      }
    };
    
    fetchCategories();
    fetchStats();
    fetchDailyDeals();
  }, []);

  const handleTariffClick = async (tariffId, categoryId) => {
    try {
      await axios.post(`${API}/analytics/click?tariff_id=${tariffId}&category_id=${categoryId}`);
    } catch (error) {
      console.error("Error tracking click:", error);
    }
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 py-20 lg:py-28">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMwMDAiIGZpbGwtb3BhY2l0eT0iLjAyIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAyLTRzLTItMi00LTItNCAwLTQgMiAwIDQgMiA0czQtMiA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-50"></div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-navy-700 mb-6 shadow-sm">
              <Award className="w-4 h-4 text-electric-500" />
              <span>Über 2 Millionen zufriedene Nutzer</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-900 mb-6 leading-tight">
              Vergleichen Sie und{" "}
              <span className="text-electric-500">sparen Sie</span>{" "}
              jeden Monat
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Unabhängiger Vergleich für Strom, Internet, Handy, Versicherungen und Kredite. 
              Transparent, neutral und kostenlos.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <Button 
                size="lg" 
                className="bg-electric-500 hover:bg-electric-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-button"
                asChild
                data-testid="hero-cta-primary"
              >
                <Link to="/strom">
                  Jetzt vergleichen
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="rounded-full px-8 py-6 text-lg font-semibold border-2"
                asChild
                data-testid="hero-cta-secondary"
              >
                <Link to="/kategorien">
                  Alle Kategorien
                </Link>
              </Button>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-signal-500" />
                <span>100% Kostenlos</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-signal-500" />
                <span>TÜV-geprüft</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-signal-500" />
                <span>Über 1.000 Anbieter</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Bento Grid */}
      <section id="categories" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
              Unsere Vergleichskategorien
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Wählen Sie eine Kategorie und finden Sie den besten Tarif für Ihre Bedürfnisse
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-64 bg-slate-100 rounded-2xl animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category, index) => {
                const Icon = categoryIcons[category.slug] || Zap;
                const colorGradient = categoryColors[category.slug] || "from-slate-400 to-slate-500";
                const isLarge = index < 2;
                
                return (
                  <Link
                    key={category.id}
                    to={`/${category.slug}`}
                    className={`group ${isLarge ? "lg:col-span-1" : ""}`}
                    data-testid={`category-card-${category.slug}`}
                  >
                    <Card className="h-full border-0 shadow-card hover:shadow-card-hover transition-all duration-300 overflow-hidden tariff-card">
                      <CardContent className="p-0 h-full">
                        <div className={`h-full min-h-[240px] flex flex-col justify-between p-8 relative overflow-hidden`}>
                          {/* Background gradient overlay */}
                          <div className={`absolute inset-0 bg-gradient-to-br ${colorGradient} opacity-5 group-hover:opacity-10 transition-opacity`}></div>
                          
                          <div className="relative z-10">
                            <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${colorGradient} mb-4`}>
                              <Icon className="w-7 h-7 text-white" />
                            </div>
                            
                            <h3 className="font-heading text-2xl font-bold text-navy-900 mb-2 group-hover:text-electric-600 transition-colors">
                              {category.name}
                            </h3>
                            
                            <p className="text-slate-600 line-clamp-2">
                              {category.description}
                            </p>
                          </div>
                          
                          <div className="relative z-10 flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
                            <span className="text-sm font-medium text-electric-600">
                              Jetzt vergleichen
                            </span>
                            <ChevronRight className="w-5 h-5 text-electric-500 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Daily Deals Section - Günün Önerileri */}
      {dailyDeals.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-electric-500 to-electric-600">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm font-medium mb-4">
                <Gift className="w-4 h-4" />
                <span>Täglich aktualisiert</span>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                Angebote des Tages
              </h2>
              <p className="text-lg text-white/80 max-w-2xl mx-auto">
                Entdecken Sie unsere handverlesenen Top-Angebote aus allen Kategorien
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {dailyDeals.slice(0, 10).map((deal) => {
                const Icon = categoryIcons[deal.category?.slug] || Zap;
                
                return (
                  <Card 
                    key={deal.id} 
                    className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white"
                    data-testid={`daily-deal-${deal.id}`}
                  >
                    <CardContent className="p-4">
                      {/* Category Badge */}
                      <div className="flex items-center justify-between mb-3">
                        <Badge className="bg-slate-100 text-slate-700 text-xs font-medium">
                          <Icon className="w-3 h-3 mr-1" />
                          {deal.category?.name}
                        </Badge>
                        {deal.is_testsieger && (
                          <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                            <Star className="w-3 h-3 mr-1 fill-yellow-500" />
                            Top
                          </Badge>
                        )}
                      </div>

                      {/* Provider Logo */}
                      <div className="flex items-center gap-2 mb-2">
                        {deal.provider?.logo_url ? (
                          <img 
                            src={deal.provider.logo_url} 
                            alt={deal.provider.name}
                            className="w-6 h-6 object-contain rounded"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center">
                            <span className="text-xs font-bold text-slate-500">
                              {deal.provider?.name?.charAt(0) || '?'}
                            </span>
                          </div>
                        )}
                        <span className="text-sm text-slate-600 truncate">
                          {deal.provider?.name}
                        </span>
                      </div>

                      {/* Tariff Name */}
                      <h3 className="font-semibold text-navy-900 text-sm mb-2 line-clamp-2 min-h-[40px]">
                        {deal.name}
                      </h3>

                      {/* Price */}
                      <div className="mb-3">
                        <div className="flex items-baseline gap-1">
                          <span className="text-xl font-bold text-electric-600">
                            {deal.price?.toFixed(2).replace('.', ',')}
                          </span>
                          <span className="text-xs text-slate-500">
                            {deal.price_unit}
                          </span>
                        </div>
                        {deal.bonus && (
                          <p className="text-xs text-signal-600 font-medium mt-1 line-clamp-1">
                            {deal.bonus}
                          </p>
                        )}
                      </div>

                      {/* CTA Button */}
                      <Button 
                        size="sm"
                        className="w-full bg-electric-500 hover:bg-electric-600 text-white text-sm"
                        onClick={() => {
                          handleTariffClick(deal.id, deal.category?.id);
                          if (deal.affiliate_link) {
                            window.open(deal.affiliate_link, '_blank');
                          }
                        }}
                        data-testid={`deal-cta-${deal.id}`}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Zum Anbieter
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* View All Categories Button */}
            <div className="text-center mt-8">
              <Button 
                size="lg"
                variant="outline"
                className="bg-white/10 border-white/30 text-white hover:bg-white hover:text-electric-600 rounded-full px-8"
                asChild
              >
                <Link to="/kategorien">
                  Alle Kategorien entdecken
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
              So funktioniert's
            </h2>
            <p className="text-lg text-slate-600">
              In nur 3 Schritten zum günstigeren Tarif
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: "1",
                title: "Vergleichen",
                description: "Wählen Sie eine Kategorie und vergleichen Sie alle verfügbaren Tarife auf einen Blick.",
                icon: TrendingDown,
              },
              {
                step: "2",
                title: "Auswählen",
                description: "Finden Sie den besten Tarif für Ihre Bedürfnisse mit unseren transparenten Bewertungen.",
                icon: Star,
              },
              {
                step: "3",
                title: "Wechseln",
                description: "Schließen Sie den Vertrag direkt beim Anbieter ab. Wir kümmern uns um den Rest.",
                icon: Clock,
              },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-electric-500 text-white text-2xl font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading text-xl font-bold text-navy-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
              Warum BestTariffeSed.de?
            </h2>
            <p className="text-lg text-slate-300 mb-12">
              Wir bieten Ihnen einen unabhängigen, transparenten Vergleich aller wichtigen Tarife in Deutschland.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { value: formatNumber(siteStats.providers) + "+", label: "Anbieter im Vergleich" },
                { value: formatNumber(siteStats.users) + "+", label: "Zufriedene Nutzer" },
                { value: "€" + Math.round(siteStats.average_savings), label: "Durchschnittliche Ersparnis" },
                { value: "100%", label: "Kostenlos & Unverbindlich" },
              ].map((stat, index) => (
                <div key={index}>
                  <div className="text-4xl font-bold text-electric-400 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-slate-400">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Affiliate Disclosure */}
      <section className="py-8 bg-slate-100 border-t border-slate-200">
        <div className="container mx-auto px-4">
          <p className="text-center text-sm text-slate-500 max-w-3xl mx-auto">
            <strong>Hinweis:</strong> Diese Website enthält Affiliate-Links. 
            Wenn Sie über diese Links Produkte oder Dienstleistungen erwerben, 
            erhalten wir möglicherweise eine Provision. Dies hat keinen Einfluss 
            auf den Preis für Sie und hilft uns, diesen Service kostenlos anzubieten.
          </p>
        </div>
      </section>
    </PublicLayout>
  );
}
